import apiClient from './client';

export type NoticeKey = 'uisp-php2shell' | 'usg-eol' | 'standalone-eol' | 'stripe-billing';

export interface LegacyGatewaySummary {
  name: string;
  model: string;
  site: string;
  online: boolean;
}

export interface UsgService {
  subscription_id: string;
  hostname: string;
  platform: 'uos' | 'standalone';
  usg_count: number;
  gateways: LegacyGatewaySummary[];
}

export interface StandaloneService {
  subscription_id: string;
  hostname: string;
  migration_path: 'self_serve' | 'assisted';
}

export type UispCrmMode = 'blocked' | 'uploads_off';

export interface UispPhp2shellService {
  subscription_id: string;
  hostname: string;
  crm: UispCrmMode;
}

interface NoticeBase {
  id: string;
  title: string;
  dismissed: boolean;
}

export type Notice =
  | (NoticeBase & { key: 'uisp-php2shell'; data: { services: UispPhp2shellService[] } })
  | (NoticeBase & { key: 'usg-eol'; data: { services: UsgService[] } })
  | (NoticeBase & { key: 'standalone-eol'; data: { services: StandaloneService[]; has_usg: boolean } })
  | (NoticeBase & { key: 'stripe-billing'; data: Record<string, never> });

// The keys the portal knows how to render. A notice with any other key (e.g. one added by
// portal-api before this portal is updated to handle it) is dropped so the bell count and the
// banner stack always agree on what's actually shown.
const KNOWN_NOTICE_KEYS = new Set<NoticeKey>(['uisp-php2shell', 'usg-eol', 'standalone-eol', 'stripe-billing']);

export async function getNotices(): Promise<Notice[]> {
  const response = await apiClient.get<{ success: boolean; notices: Notice[] }>('/api/v1/notices');
  if (!response.data.success) throw new Error('Failed to load notices');
  return response.data.notices.filter((n) => KNOWN_NOTICE_KEYS.has(n.key));
}

export async function dismissNotice(id: string): Promise<void> {
  await apiClient.post(`/api/v1/notices/${encodeURIComponent(id)}/dismiss`);
}
